import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useRestaurants } from '../hooks/useAPI';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Search, Star, Clock, MapPin, RefreshCw } from 'lucide-react';

const RestaurantsPage = () => {
  const { t, language } = useLanguage();
  const { restaurants, isLoading, refresh } = useRestaurants();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('all');

  const cuisineTypes = ['all', ...new Set(restaurants.map(r => r.cuisine_type))];

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         restaurant.cuisine_type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCuisine = selectedCuisine === 'all' || restaurant.cuisine_type === selectedCuisine;
    return matchesSearch && matchesCuisine;
  });

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#FF6B35]/10 to-transparent py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-4xl md:text-5xl font-bold text-[#004E64]" data-testid="restaurants-title">
              {t('restaurants')}
            </h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refresh()}
              className="text-[#9FA2B4] hover:text-[#004E64]"
              data-testid="refresh-button"
            >
              <RefreshCw size={18} className="mr-2" />
              {t('language') === 'pt' ? 'Atualizar' : 'Refresh'}
            </Button>
          </div>
          <p className="text-[#9FA2B4] text-lg mb-8">
            {restaurants.length} {t('restaurants').toLowerCase()}
          </p>
          
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9FA2B4]" size={20} />
              <Input 
                type="text"
                placeholder={t('search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 rounded-full border-[#004E64]/10 bg-white"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-2">
              {cuisineTypes.map(cuisine => (
                <Button
                  key={cuisine}
                  variant={selectedCuisine === cuisine ? 'default' : 'outline'}
                  className={`rounded-full whitespace-nowrap ${
                    selectedCuisine === cuisine 
                      ? 'bg-[#FF6B35] hover:bg-[#E85D2A]' 
                      : 'border-[#004E64]/10 text-[#004E64]'
                  }`}
                  onClick={() => setSelectedCuisine(cuisine)}
                  data-testid={`filter-${cuisine}`}
                >
                  {cuisine === 'all' ? t('all') : cuisine}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Restaurant Grid */}
      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(6).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-72 rounded-3xl" />
            ))}
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#9FA2B4] text-lg">No restaurants found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...filteredRestaurants].sort((a, b) => ((b.is_open_now !== false) - (a.is_open_now !== false))).map(restaurant => (
              <Link key={restaurant.restaurant_id} to={`/restaurants/${restaurant.restaurant_id}`}>
                <Card 
                  className="restaurant-card bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-[#004E64]/5 group cursor-pointer h-full"
                  data-testid={`restaurant-card-${restaurant.restaurant_id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={restaurant.cover_image || restaurant.logo} 
                      alt={restaurant.name}
                      className={`restaurant-image w-full h-full object-cover ${restaurant.is_open_now === false ? 'grayscale' : ''}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <Badge className={`absolute top-3 left-3 text-white ${restaurant.is_active === false || restaurant.is_out_of_stock ? 'bg-red-500 hover:bg-red-500' : restaurant.is_open_now !== false ? 'bg-green-600 hover:bg-green-600' : 'bg-gray-600 hover:bg-gray-600'}`} data-testid={`open-badge-${restaurant.restaurant_id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${restaurant.is_open_now !== false ? 'bg-white animate-pulse' : 'bg-white/60'}`} />
                      {restaurant.is_active === false || restaurant.is_out_of_stock
                        ? (language === 'pt' ? 'Indisponível' : 'Unavailable')
                        : restaurant.is_open_now !== false
                          ? (language === 'pt' ? 'Aberto' : 'Open')
                          : (language === 'pt' ? 'Fechado' : 'Closed')}
                    </Badge>
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1">
                      <Star className="text-yellow-500 fill-yellow-500" size={16} />
                      <span className="font-bold text-[#004E64]">{restaurant.rating}</span>
                    </div>
                    <div className="absolute bottom-3 left-3">
                      <Badge className="bg-[#FF6B35] hover:bg-[#FF6B35]">
                        {restaurant.cuisine_type}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-bold text-[#004E64] text-xl mb-2 group-hover:text-[#FF6B35] transition-colors">
                      {restaurant.name}
                    </h3>
                    <p className="text-[#9FA2B4] text-sm mb-4 line-clamp-2">
                      {restaurant.description}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-4 text-[#9FA2B4]">
                        <span className="flex items-center gap-1">
                          <Clock size={16} />
                          {restaurant.delivery_time}
                        </span>
                      </div>
                      <span className="text-[#FF6B35] font-semibold">{t('deliveryFrom')}</span>
                    </div>
                    {restaurant.address && (
                      <div className="flex items-center gap-1 text-sm text-[#9FA2B4] mt-3">
                        <MapPin size={14} />
                        {restaurant.address}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantsPage;
